import {
  ActivityDataConfigProps,
  ActivityInformationElementConfig,
  ActivityInformationElementId,
  ActivityInformationElementOptions,
  AppConfigStates,
} from "types/appFunctionality";

type ActivityConfigField = {
  [Key in keyof ActivityDataConfigProps]-?: ActivityDataConfigProps[Key] extends AppConfigStates
    ? Key
    : never;
}[keyof ActivityDataConfigProps];

export type ActivityFormConfig = {
  isVisible: (field: ActivityConfigField) => boolean;
  isDisabled: (field: ActivityConfigField) => boolean;
  isEnabled: (field: ActivityConfigField) => boolean;
  defaultTypeId: string;
};

export type ActivityFormSectionId = "information" | "species" | "screening";

export type ActivityFormSection = {
  id: ActivityFormSectionId;
  label: string;
  disabled: boolean;
};

export const DEFAULT_ACTIVITY_INFORMATION_ELEMENT_ORDER: ActivityInformationElementId[] = [
  "title",
  "description",
  "type",
  "biomass",
  "intervention",
  "references",
  "responsibility",
  "scheduleStatus",
  "eudrReference",
  "screeningActivation",
  "activityId",
];

export type ActivityInformationElement = {
  id: ActivityInformationElementId;
  options?: ActivityInformationElementOptions;
};

/**
 * Ordering configuration is keyed by ID rather than an array. This avoids the
 * app config's array-union merge semantics changing an explicitly chosen order.
 * Configured blocks are rendered first by numeric order; all other blocks keep
 * their legacy relative order afterwards.
 */
export const getActivityInformationElements = (
  config?: Partial<Record<ActivityInformationElementId, ActivityInformationElementConfig>>,
): ActivityInformationElement[] => {
  const getElementConfig = (
    id: ActivityInformationElementId,
  ): ActivityInformationElementConfig | undefined => (config ? config[id] : undefined);
  const configured = DEFAULT_ACTIVITY_INFORMATION_ELEMENT_ORDER
    .map((id, defaultIndex) => ({ id, defaultIndex, ...getElementConfig(id) }))
    .filter((element): element is typeof element & { order: number } => typeof element.order === "number")
    .sort((a, b) => a.order - b.order || a.defaultIndex - b.defaultIndex);
  const configuredIds = new Set(configured.map(({ id }) => id));
  const remaining = DEFAULT_ACTIVITY_INFORMATION_ELEMENT_ORDER
    .filter((id) => !configuredIds.has(id))
    .map((id) => ({ id, ...getElementConfig(id) }));

  return [...configured, ...remaining].map(({ id, options }) => ({ id, options }));
};

/**
 * Keeps activity form visibility and editability decisions consistent across
 * the dialog's fields, tabs, and future subscription-specific sections.
 */
export const createActivityFormConfig = (
  config: ActivityDataConfigProps,
): ActivityFormConfig => ({
  isVisible: (field) => config[field] !== "hidden",
  isDisabled: (field) => config[field] === "disabled",
  isEnabled: (field) => config[field] === "enabled",
  defaultTypeId: config.defaultTypeId,
});

type InitialFullBiomassScreeningEnabledProps = {
  screeningToggleEnabled: boolean;
  riskAssessment?: string[];
  fullBiomassScreeningEnabled?: boolean;
  hasBiomassScreening: boolean;
};

/**
 * Activities saved before the explicit toggle was introduced only have
 * biomass-screening data. Preserve their previous behavior, while allowing a
 * saved false value from newer activities to remain false when reopened.
 */
export const getInitialFullBiomassScreeningEnabled = ({
  screeningToggleEnabled,
  riskAssessment,
  fullBiomassScreeningEnabled,
  hasBiomassScreening,
}: InitialFullBiomassScreeningEnabledProps): boolean => {
  if (screeningToggleEnabled && riskAssessment?.includes("certified")) return false;

  if (typeof fullBiomassScreeningEnabled === "boolean") return fullBiomassScreeningEnabled;

  return hasBiomassScreening;
};

type CreateActivityFormSectionsProps = {
  config: ActivityFormConfig;
  country: string;
  withLogging: boolean;
  fullBiomassScreeningEnabled: boolean;
  labels: Record<ActivityFormSectionId, string>;
};

/**
 * The visible panels must be defined from the same list as the tabs, otherwise
 * their indexes can drift when a subscription hides a section.
 */
export const createActivityFormSections = ({
  config,
  country,
  withLogging,
  fullBiomassScreeningEnabled,
  labels,
}: CreateActivityFormSectionsProps): ActivityFormSection[] => [
  { id: "information", label: labels.information, disabled: false },
  ...(config.isVisible("species")
    ? [{ id: "species" as const, label: labels.species, disabled: !withLogging }]
    : []),
  ...(country === "DK" &&
  config.isVisible("screening") &&
  (!config.isVisible("infoBiomassScreeningEnableScreening") || fullBiomassScreeningEnabled)
    ? [
        {
          id: "screening" as const,
          label: labels.screening,
          disabled: !withLogging || config.isDisabled("screening"),
        },
      ]
    : []),
];
